/**
 * BookingModal Component
 * 
 * Unified modal for booking, rescheduling, and cancelling classes.
 * Uses a single component with mode prop to handle both flows.
 * 
 * Features:
 * - SlotPickerCalendar for visual slot selection
 * - Confirmation step before booking
 * - Cancel flow when tapping a booked (green) slot
 * - Timezone display on confirmation
 * - Loading and error states
 * - Success feedback
 * - Shows parent's existing bookings on calendar
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { AlertCircle, CheckCircle, Calendar, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from '@/lib/i18n';
import { 
  formatSlotTime, 
  getSlotTypeLabel,
  type AvailableSlot 
} from '@/lib/scheduling';
import { getTimezoneLabel, getTimezoneAbbreviation } from '@/lib/timezones';
import { SlotPickerCalendar, type BookedSlotInfo } from './SlotPickerCalendar';

type BookingMode = 'book' | 'reschedule';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  childId: string;
  childName?: string;
  mode: BookingMode;
  existingBookingId?: string;
  existingSlotId?: string;
}

type Step = 'select' | 'confirm' | 'success' | 'cancel-confirm' | 'cancel-success';

export const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  childId,
  childName,
  mode,
  existingBookingId,
  existingSlotId,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('select');
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [cancelTarget, setCancelTarget] = useState<BookedSlotInfo | null>(null);
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [selectedTimezone, setSelectedTimezone] = useState(browserTimezone);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingBookings, setExistingBookings] = useState<string[]>([]);

  useEffect(() => {
    let isCancelled = false;
    
    const fetchExistingBookings = async () => {
      if (!childId || !isOpen) return;
      
      const { data, error } = await supabase
        .from('scheduled_classes')
        .select('slot_id')
        .eq('child_id', childId)
        .in('status', ['scheduled', 'confirmed']);
      
      if (isCancelled) return;
      
      if (!error && data) {
        setExistingBookings(data.map(d => d.slot_id).filter(Boolean) as string[]);
      }
    };
    
    fetchExistingBookings();
    
    return () => {
      isCancelled = true;
    };
  }, [childId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedSlot(null);
      setCancelTarget(null);
      setError(null);
    }
  }, [isOpen]);

  const handleSlotSelect = useCallback((slot: AvailableSlot) => {
    setSelectedSlot(slot);
    setStep('confirm');
    setError(null);
  }, []);

  const handleBookedSlotCancel = useCallback((info: BookedSlotInfo) => {
    setCancelTarget(info);
    setStep('cancel-confirm');
    setError(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedSlot) return;
    
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error(t('Not authenticated'));
      }

      const requestBody = mode === 'book'
        ? { action: 'book', childId, slotId: selectedSlot.id }
        : { action: 'reschedule', bookingId: existingBookingId, newSlotId: selectedSlot.id };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scheduling-book`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || t('Booking failed'));
      }

      setStep('success');
      
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('[BookingModal] Error:', err);
      setError(err instanceof Error ? err.message : t('An error occurred'));
    } finally {
      setLoading(false);
    }
  }, [selectedSlot, mode, childId, existingBookingId, onSuccess, onClose, t]);

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelTarget) return;

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error(t('Not authenticated'));
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scheduling-book`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'cancel',
            bookingId: cancelTarget.scheduledClassId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || t('Cancellation failed'));
      }

      setStep('cancel-success');

      setTimeout(() => {
        setStep('select');
        setCancelTarget(null);
        onSuccess();
      }, 2000);
    } catch (err) {
      console.error('[BookingModal] Cancel error:', err);
      setError(err instanceof Error ? err.message : t('An error occurred'));
    } finally {
      setLoading(false);
    }
  }, [cancelTarget, onSuccess, t]);

  const formattedSlot = selectedSlot 
    ? formatSlotTime(selectedSlot.startTime, selectedTimezone)
    : null;

  const formattedCancelSlot = cancelTarget
    ? formatSlotTime(cancelTarget.startTime, selectedTimezone)
    : null;

  const title = mode === 'book' ? t('Book a Lesson') : t('Reschedule Lesson');
  const tzAbbr = getTimezoneAbbreviation(selectedTimezone);
  const tzLabel = getTimezoneLabel(selectedTimezone);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="2xl"
      className="max-h-[100dvh] sm:max-h-[90vh]"
    >
      <div className="min-h-[400px] sm:min-h-[500px]">
        {/* Step: Select Slot */}
        {step === 'select' && (
          <SlotPickerCalendar
            onSlotSelect={handleSlotSelect}
            onBookedSlotCancel={handleBookedSlotCancel}
            childId={childId}
            existingSlotId={existingSlotId}
            existingBookings={existingBookings}
            timezone={selectedTimezone}
            onTimezoneChange={setSelectedTimezone}
          />
        )}

        {/* Step: Confirm Booking */}
        {step === 'confirm' && selectedSlot && formattedSlot && (() => {
          const cancelHours = selectedSlot.minCancellationNoticeHours ?? 24;
          return (
          <div className="space-y-6">
            <div className="p-6 rounded-xl bg-black/[0.03] border border-black/[0.08]">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-full bg-[#111111]/10">
                  <Calendar className="w-6 h-6 text-[#1A1A1A]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1C1C1E]">
                    {mode === 'book' ? t('Confirm Booking') : t('Confirm Reschedule')}
                  </h3>
                  <p className="text-sm text-[#8E8E93]">
                    {mode === 'book' 
                      ? t('Please confirm the following booking details')
                      : t('Your lesson will be moved to the new time')}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {childName && (
                  <div className="flex justify-between py-2 border-b border-black/[0.08]">
                    <span className="text-[#8E8E93]">{t('Student')}</span>
                    <span className="text-[#1C1C1E] font-medium">{childName}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Session Type')}</span>
                  <span className="text-[#1C1C1E] font-medium">{getSlotTypeLabel(selectedSlot.slotType)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Date')}</span>
                  <span className="text-[#1C1C1E] font-medium">{formattedSlot.date}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Time')}</span>
                  <span className="text-[#1C1C1E] font-medium">{formattedSlot.time}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Time Zone')}</span>
                  <span className="text-[#1C1C1E] font-medium">{tzAbbr} ({tzLabel})</span>
                </div>
                {selectedSlot.instructorName && (
                  <div className="flex justify-between py-2">
                    <span className="text-[#8E8E93]">{t('Instructor')}</span>
                    <span className="text-[#1C1C1E] font-medium">{selectedSlot.instructorName}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>{t('Cancellation Policy:')}</strong> {t('Lessons can be cancelled free of charge up to')} {cancelHours} {t('hours before the scheduled time. Cancellations within')} {cancelHours} {t('hours will be counted toward your monthly lesson bill.')}
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-600">{error}</span>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setStep('select')}
                disabled={loading}
                className="flex-1"
              >
                {t('Back')}
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={loading}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    {mode === 'book' ? t('Booking...') : t('Rescheduling...')}
                  </>
                ) : (
                  mode === 'book' ? t('Confirm Booking') : t('Confirm Reschedule')
                )}
              </Button>
            </div>
          </div>
          );
        })()}

        {/* Step: Confirm Cancel */}
        {step === 'cancel-confirm' && cancelTarget && formattedCancelSlot && (() => {
          const cancelHours = cancelTarget.minCancellationNoticeHours ?? 24;
          return (
          <div className="space-y-6">
            <div className="p-6 rounded-xl bg-black/[0.03] border border-black/[0.08]">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-full bg-red-50">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1C1C1E]">{t('Cancel Lesson?')}</h3>
                  <p className="text-sm text-[#8E8E93]">{t('This will cancel your booked lesson')}</p>
                </div>
              </div>

              <div className="space-y-3">
                {childName && (
                  <div className="flex justify-between py-2 border-b border-black/[0.08]">
                    <span className="text-[#8E8E93]">{t('Student')}</span>
                    <span className="text-[#1C1C1E] font-medium">{childName}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Session Type')}</span>
                  <span className="text-[#1C1C1E] font-medium">{getSlotTypeLabel(cancelTarget.slotType)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Date')}</span>
                  <span className="text-[#1C1C1E] font-medium">{formattedCancelSlot.date}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Time')}</span>
                  <span className="text-[#1C1C1E] font-medium">{formattedCancelSlot.time}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#8E8E93]">{t('Time Zone')}</span>
                  <span className="text-[#1C1C1E] font-medium">{tzAbbr} ({tzLabel})</span>
                </div>
                {cancelTarget.instructorName && (
                  <div className="flex justify-between py-2">
                    <span className="text-[#8E8E93]">{t('Instructor')}</span>
                    <span className="text-[#1C1C1E] font-medium">{cancelTarget.instructorName}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>{t('Cancellation Policy:')}</strong> {t('Lessons can be cancelled free of charge up to')} {cancelHours} {t('hours before the scheduled time. Cancellations within')} {cancelHours} {t('hours will be counted toward your monthly lesson bill.')}
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-600">{error}</span>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => { setStep('select'); setCancelTarget(null); setError(null); }}
                disabled={loading}
                className="flex-1"
              >
                {t('Back')}
              </Button>
              <Button
                variant="primary"
                onClick={handleCancelConfirm}
                disabled={loading}
                className="flex-1 !bg-red-600 hover:!bg-red-500"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    {t('Cancelling...')}
                  </>
                ) : (
                  t('Cancel Lesson')
                )}
              </Button>
            </div>
          </div>
          );
        })()}

        {/* Step: Book/Reschedule Success */}
        {step === 'success' && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="p-4 rounded-full bg-green-50 mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1C1C1E] mb-2">
              {mode === 'book' ? t('Lesson Booked!') : t('Lesson Rescheduled!')}
            </h3>
            <p className="text-[#8E8E93] text-center">
              {mode === 'book' 
                ? t('You will receive a confirmation email shortly.')
                : t('Your calendar has been updated with the new time.')}
            </p>
          </div>
        )}

        {/* Step: Cancel Success */}
        {step === 'cancel-success' && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="p-4 rounded-full bg-green-50 mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1C1C1E] mb-2">{t('Lesson Cancelled')}</h3>
            <p className="text-[#8E8E93] text-center">
              {t('Your lesson has been cancelled successfully.')}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BookingModal;
